// File: apps/fe/src/shared/components/VideoPlayer/context/player-context.tsx
import React, {
  createContext,
  useContext,
  useMemo,
  useReducer,
  useCallback,
  useEffect,
} from 'react';
import { PlayerConfig, PlaybackConfig, ProgressPayload, PlayerContextValue } from '../types/player';
import { usePlayer } from '../hooks/use-player';

// Create context with a default empty value
const PlayerContext = createContext<PlayerContextValue | undefined>(undefined);

// Define the state interface
export interface PlayerContextState {
  aspectRatio: number | null;
  isVerticalVideo: boolean;
  handleAspectRatio: boolean;
}

// Define the action type
type PlayerAction = { type: 'SET_ASPECT_RATIO'; payload: number };

// Define the default state
export const defaultPlayerContextState: PlayerContextState = {
  aspectRatio: null,
  isVerticalVideo: false,
  handleAspectRatio: false,
};

export interface PlayerProviderProps extends PlayerConfig, PlaybackConfig {
  children: React.ReactNode;
  onNextVideo?: () => void;
  endScreen?: boolean;
  onProgressSave?: (progress: ProgressPayload) => void;
  simpleControls?: boolean;
  aspectRatio?: number | null;
  handleAspectRatio?: boolean;
}
// Reducer function to handle state updates
export function playerReducer(state: PlayerContextState, action: PlayerAction): PlayerContextState {
  switch (action.type) {
    case 'SET_ASPECT_RATIO':
      return {
        ...state,
        aspectRatio: action.payload,
        // Only calculate isVerticalVideo if handleAspectRatio is true
        isVerticalVideo: state.handleAspectRatio ? action.payload < 1 : false,
      };
    default:
      return state;
  }
}

export const PlayerProvider: React.FC<PlayerProviderProps> = ({
  children,
  autoPlay = false,
  playbackId,
  playbackToken,
  videoId,
  videoTitle,
  storyboardMetadataUrl,
  muxMetadata,
  onNextVideo,
  endScreen = false,
  lessonDocumentId,
  onProgressSave,
  simpleControls = false,
  aspectRatio = null,
  handleAspectRatio = false,
}) => {
  const handleNextVideo = () => {
    if (onNextVideo) {
      onNextVideo();
    }
  };
  // Initialize the player hook with storyboardMetadataUrl
  const player = usePlayer({
    autoPlay,
    lessonDocumentId,
    onNextVideo: handleNextVideo,
    onProgressSave,
    playbackConfig: {
      playbackId,
      playbackToken,
      videoId,
      videoTitle,
      storyboardMetadataUrl,
      muxMetadata,
    },
  });

  const [state, dispatch] = useReducer(playerReducer, {
    ...defaultPlayerContextState,
    aspectRatio: aspectRatio,
    // Only consider vertical if handleAspectRatio is true
    isVerticalVideo: handleAspectRatio && aspectRatio !== null ? aspectRatio < 1 : false,
    handleAspectRatio: handleAspectRatio,
  });

  const setAspectRatio = useCallback((ratio: number) => {
    dispatch({ type: 'SET_ASPECT_RATIO', payload: ratio });
  }, []);

  // Update aspect ratio when prop changes
  useEffect(() => {
    if (aspectRatio !== null) {
      setAspectRatio(aspectRatio);
    }
  }, [aspectRatio, setAspectRatio]);

  // Create the context value with memoization to prevent unnecessary renders
  const value = useMemo<PlayerContextValue>(
    () => ({
      ...player,
      handleNextVideo,
      playbackConfig: {
        playbackId,
        playbackToken,
        videoId,
        videoTitle,
        storyboardMetadataUrl,
        muxMetadata,
      },
      simpleControls,
      get videoElement() {
        return player.playerRef.current;
      },
      aspectRatio: state.aspectRatio,
      isVerticalVideo: state.isVerticalVideo,
      handleAspectRatio: state.handleAspectRatio,
      setAspectRatio,
    }),
    [
      player,
      handleNextVideo,
      playbackId,
      playbackToken,
      videoId,
      videoTitle,
      storyboardMetadataUrl,
      muxMetadata,
      simpleControls,
      setAspectRatio,
      state.aspectRatio,
      state.isVerticalVideo,
      state.handleAspectRatio,
    ],
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
};

// Custom hook for using the player context
export const usePlayerContext = (): PlayerContextValue => {
  const context = useContext(PlayerContext);

  if (context === undefined) {
    throw new Error('usePlayerContext must be used within a PlayerProvider');
  }

  return context;
};
